import React, { useState } from 'react';
import { Box, Button, IconButton, Modal, Typography, makeStyles } from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import {
  getInternalChatMediaKind,
  resolveInternalChatMediaFetchPath,
  resolveInternalChatMediaUrl,
} from '../../utils/internalChatMedia';
import { downloadResource } from '../../utils';
import AuthenticatedImage from '../AuthenticatedImage';
import AuthenticatedVideo from '../AuthenticatedVideo';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: 6,
    maxWidth: '100%',
  },
  img: {
    maxWidth: '100%',
    maxHeight: 220,
    borderRadius: 8,
    display: 'block',
    cursor: 'pointer',
    objectFit: 'contain',
  },
  video: {
    maxWidth: '100%',
    maxHeight: 240,
    borderRadius: 8,
    display: 'block',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalInner: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    maxWidth: '92vw',
  },
  modalImgWrap: {
    maxWidth: '90vw',
    maxHeight: '82vh',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default function InternalChatMessageMedia({ mediaPath, mediaName, bubbleTone }) {
  const classes = useStyles();
  const [previewOpen, setPreviewOpen] = useState(false);

  const raw = mediaPath || mediaName;
  if (!raw) return null;

  const fetchPath = resolveInternalChatMediaFetchPath(mediaPath || mediaName);
  const downloadUrl = resolveInternalChatMediaUrl(mediaPath || mediaName);
  if (!fetchPath) return null;

  const kind = getInternalChatMediaKind(mediaPath, mediaName);
  if (kind === 'audio') return null;

  const displayName =
    mediaName || (typeof mediaPath === 'string' ? mediaPath.split('/').pop() : '') || 'arquivo';

  const imgPlaceholder =
    bubbleTone === 'outgoing'
      ? 'bubbleOutgoing'
      : bubbleTone === 'incoming'
        ? 'bubbleIncoming'
        : 'dark';

  if (kind === 'image') {
    return (
      <Box className={classes.root}>
        <AuthenticatedImage
          className={`${classes.img} internal-chat-media-preview`}
          imageUrl={fetchPath}
          alt={displayName}
          onClick={() => setPreviewOpen(true)}
          placeholderVariant={imgPlaceholder}
        />
        <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} className={classes.modal}>
          <div className={classes.modalInner}>
            <div className={classes.modalImgWrap}>
              <AuthenticatedImage
                imageUrl={fetchPath}
                alt={displayName}
                style={{ maxWidth: '90vw', maxHeight: '82vh', objectFit: 'contain' }}
              />
            </div>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={() => downloadResource(downloadUrl || fetchPath, displayName)}
            >
              Baixar
            </Button>
          </div>
        </Modal>
      </Box>
    );
  }

  if (kind === 'video') {
    return (
      <Box className={classes.root}>
        <AuthenticatedVideo className={classes.video} videoUrl={fetchPath} controls playsInline />
      </Box>
    );
  }

  const isPdf = /\.pdf(\?|#|$)/i.test(displayName) || /\.pdf(\?|#|$)/i.test(mediaPath || '');
  const fileExtension = displayName.includes('.')
    ? displayName.split('.').pop().toUpperCase()
    : 'Arquivo';

  return (
    <Box className={classes.root}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'linear-gradient(180deg, #f8fafc 0%, #eef3f7 100%)',
          border: '1px solid #dce5ec',
          padding: '10px 12px',
          borderRadius: 10,
          maxWidth: 280,
          cursor: 'pointer',
        }}
        onClick={() => downloadResource(downloadUrl || fetchPath, displayName)}
        title="Clique para baixar"
      >
        <div
          style={{
            width: 44,
            height: 44,
            backgroundColor: '#e2e8f0',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isPdf ? (
            <PictureAsPdfIcon style={{ fontSize: 24, color: '#d93025' }} />
          ) : (
            <InsertDriveFileIcon style={{ fontSize: 24, color: '#64748b' }} />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Typography
            style={{
              fontSize: 13,
              color: '#1f2937',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={displayName}
          >
            {displayName}
          </Typography>
          <Typography style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {fileExtension}
          </Typography>
        </div>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            downloadResource(downloadUrl || fetchPath, displayName);
          }}
          title="Download"
          style={{ backgroundColor: '#e2e8f0', color: '#64748b', flexShrink: 0 }}
        >
          <GetAppIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>
    </Box>
  );
}
